import {Link, WithRouterProps} from 'react-router';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import FeatureFlag from 'client/components/FeatureFlag/FeatureFlag.react';
import {Icon, Tab} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {getStudentQuery} from 'client/Classrooms/StudentDashboard/StudentDashboard.queries.js';
import {LoadingSpinner} from '@albert-io/atomic';
import StudentHomepageHeader from './StudentHomepageHeader';
import StudentHomepageSidebar from './StudentHomepageSidebar';
import {useStudentDashboard} from 'client/Classrooms/StudentDashboard/StudentDashboard.store';

interface TabConfig {
  icon: IconProp;
  label: string;
  to: string;
}

interface Props extends PropsWithChildrenRequired, WithRouterProps {}

export default function StudentHomepage({children, location}: Props) {
  const [isLoading, setIsLoading] = useState(true);

  const {setStudent} = useStudentDashboard(
    useShallow((state) => ({
      setStudent: state.setStudent
    }))
  );

  useEffect(() => {
    getStudentQuery()
      .getResourcePromise()
      .then((student) => {
        setStudent(student);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabConfigs: TabConfig[] = [
    {
      icon: ['fal', 'file-alt'],
      label: 'Assignments',
      to: '/home/assignments'
    },
    {
      icon: ['fal', 'sparkles'],
      label: 'Skill Path',
      to: '/home/skill-path'
    },
    {
      icon: ['fal', 'pencil'],
      label: 'Independent Practice',
      to: '/home/independent-practice'
    }
  ];

  return (
    <FeatureFlag name='new_student_dashboard'>
      <div className='-mt-[60px] grid min-h-[calc(100vh-60px)] grid-cols-4 grid-rows-[min-content_1fr] background-subtle'>
        {isLoading ? (
          <LoadingSpinner className='col-span-4 row-span-2 self-center' size={5} />
        ) : (
          <>
            <StudentHomepageHeader />
            <StudentHomepageSidebar />
            <div className='col-span-3 mx-x2 my-x3 rounded-3xl background-primary'>
              <div className='flex items-start'>
                {tabConfigs.map(({icon, label, to}) => (
                  <Tab
                    as={Link}
                    className='px-x4 py-x3'
                    contentWrapperClassName='flex gap-x1 items-center'
                    isActive={location.pathname.includes(to)}
                    key={to}
                    to={to}
                  >
                    <Icon icon={icon} />
                    {label}
                  </Tab>
                ))}
              </div>
              <div className='flex h-full items-center justify-center'>{children}</div>
            </div>
          </>
        )}
      </div>
    </FeatureFlag>
  );
}
